import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/admin/Layout';
import { Search, Edit, Trash2, Eye, X, Check, Send } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getCountryTranslations } from '../../translations/countryTranslations';
import { useLanguage } from '../../contexts/LanguageContext';

interface User {
  id: string;
  full_name: string;
  email: string;
  country: string | null;
  total_points: number;
  water_volume_consumed: number;
  created_at: string;
}

interface FilterOptions {
  country: string | null;
  minPoints: number | null;
  maxPoints: number | null;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const Users: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    points: 0
  });
  const [userRewards, setUserRewards] = useState<any[]>([]);
  const [userSurveys, setUserSurveys] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({
    country: null,
    minPoints: null,
    maxPoints: null,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  const countryOptions = [
    { value: '', label: t('admin.allCountries') },
    ...getCountryTranslations(currentLanguage)
  ];

  const sortOptions = [
    { value: 'created_at', label: t('admin.joinedDate') },
    { value: 'full_name', label: t('admin.name') },
    { value: 'total_points', label: t('admin.points') },
    { value: 'water_volume_consumed', label: t('admin.waterVolume') }
  ];

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Get all profiles with total_points and water_volume_consumed
      let query = supabase
        .from('profiles')
        .select('id, full_name, email, country, created_at, total_points, water_volume_consumed');
      
      // Apply filters
      if (filters.country) {
        query = query.eq('country', filters.country);
      }
      
      // Only apply database sorting for columns that exist in the profiles table
      if (['created_at', 'full_name', 'total_points', 'water_volume_consumed'].includes(filters.sortBy)) {
        query = query.order(filters.sortBy, { ascending: filters.sortOrder === 'asc' });
      }
      
      const { data: profiles, error: profilesError } = await query;
      
      if (profilesError) throw profilesError;
      
      if (!profiles) {
        setUsers([]);
        return;
      }

      // Format the data for display
      let usersWithData = profiles.map(profile => {
        return {
          ...profile,
          water_volume: (profile.water_volume_consumed || 0) / 1000 // Convert to liters and round to 1 decimal
        };
      });
      
      // Apply points filters
      if (filters.minPoints !== null) {
        usersWithData = usersWithData.filter(user => user.total_points >= filters.minPoints!);
      }
      if (filters.maxPoints !== null) {
        usersWithData = usersWithData.filter(user => user.total_points <= filters.maxPoints!);
      }

      setUsers(usersWithData);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('admin.errorFetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsers = users.filter(user => 
    (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.country || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserSelection = (userId: string) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleSendMessage = async () => {
    if (selectedUsers.length === 0 || !messageTitle.trim() || !messageContent.trim()) {
      toast.error(t('admin.pleaseAllFields'));
      return;
    }

    try {
      setSendingMessage(true);

      const { data: adminData } = await supabase.auth.getUser();
      
      // Create notifications for all selected users
      const notifications = selectedUsers.map(userId => ({
        admin_id: adminData.user?.id,
        user_id: userId,
        title: messageTitle,
        message: messageContent
      }));

      const { error } = await supabase
        .from('admin_notifications')
        .insert(notifications);

      if (error) throw error;

      toast.success(t('admin.messagesSent'));
      setMessageTitle('');
      setMessageContent('');
      setSelectedUsers([]);
      setIsMessageModalOpen(false);
    } catch (error) {
      console.error('Error sending messages:', error);
      toast.error(t('admin.errorSendingMessages'));
    } finally {
      setSendingMessage(false);
    }
  };

  const openViewModal = async (user: User) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
    
    try {
      // Fetch user rewards
      const { data: rewards, error: rewardsError } = await supabase
        .from('reward_requests')
        .select('*, rewards(name)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (rewardsError) throw rewardsError;
      
      setUserRewards(rewards || []);
      
      // Fetch user surveys
      const { data: surveys, error: surveysError } = await supabase
        .from('completed_surveys')
        .select('*, surveys(title)')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });
      
      if (surveysError) throw surveysError;
      
      setUserSurveys(surveys || []);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      full_name: user.full_name,
      points: user.total_points
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUser) return;
    
    try {
      // Update user profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          full_name: editForm.full_name,
          total_points: editForm.points 
        })
        .eq('id', selectedUser.id);
      
      if (profileError) throw profileError;
      
      // If points changed, add a points adjustment
      if (editForm.points !== selectedUser.total_points) {
        const pointsDiff = editForm.points - selectedUser.total_points;
        
        if (pointsDiff !== 0) {
          const { error: pointsError } = await supabase
            .from('user_points')
            .insert([{
              user_id: selectedUser.id,
              points: pointsDiff,
              source: 'admin_adjustment',
              description: 'Admin adjustment'
            }]);
          
          if (pointsError) throw pointsError;
        }
      }
      
      toast.success(t('admin.userUpdated'));
      setIsEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(t('admin.errorUpdatingUser'));
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      // Delete user from auth
      const { error } = await supabase.auth.admin.deleteUser(selectedUser.id);
      
      if (error) throw error;
      
      toast.success(t('admin.userDeleted'));
      setIsDeleteModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(t('admin.errorDeletingUser'));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  return (
    <AdminLayout title={t('admin.userManagement')}>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 md:mb-0">{t('admin.allUsers')}</h3>
          
          {/* Search and Filters */}
          <div className="space-y-4 md:space-y-0 md:flex md:items-center md:space-x-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('admin.searchUsers')}
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
              />
            </div>

            {/* Country Filter */}
            <Select
              className="w-48"
              placeholder={t('admin.filterByCountry')}
              options={countryOptions}
              value={countryOptions.find(option => option.value === filters.country)}
              onChange={(option) => setFilters(prev => ({ ...prev, country: option?.value || null }))}
              isClearable
            />

            {/* Points Range */}
            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder={t('admin.minPoints')}
                value={filters.minPoints || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, minPoints: e.target.value ? Number(e.target.value) : null }))}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md"
              />
              <span>-</span>
              <input
                type="number"
                placeholder={t('admin.maxPoints')}
                value={filters.maxPoints || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, maxPoints: e.target.value ? Number(e.target.value) : null }))}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Sort */}
            <div className="flex items-center space-x-2">
              <Select
                className="w-48"
                placeholder={t('admin.sortBy')}
                options={sortOptions}
                value={sortOptions.find(option => option.value === filters.sortBy)}
                onChange={(option) => setFilters(prev => ({ ...prev, sortBy: option?.value || 'created_at' }))}
              />
              <button
                onClick={() => setFilters(prev => ({ ...prev, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }))}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {filters.sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="mb-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={selectedUsers.length === filteredUsers.length}
                onChange={handleSelectAllUsers}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">{t('admin.selectAll')}</span>
            </label>
            <span className="text-sm text-gray-500">
              {selectedUsers.length} {t('admin.usersSelected')}
            </span>
          </div>
          
          {selectedUsers.length > 0 && (
            <button
              onClick={() => setIsMessageModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Send className="h-5 w-5 mr-2" />
              {t('admin.sendMessage')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? t('admin.noUsersFound') : t('admin.noUsers')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="sr-only">{t('admin.select')}</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.email')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.country')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.points')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.waterVolume')} (L)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.joinedDate')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleUserSelection(user.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.country}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.total_points}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{(user.water_volume_consumed / 1000).toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(user.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openViewModal(user)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title={t('admin.view')}
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                        title={t('admin.edit')}
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUsers([user.id]);
                          setIsMessageModalOpen(true);
                        }}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title={t('admin.sendMessage')}
                      >
                        <Send className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(user)}
                        className="text-red-600 hover:text-red-900"
                        title={t('admin.delete')}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Message Modal */}
      {isMessageModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t('admin.sendMessageToUsers', { count: selectedUsers.length })}
                    </h3>
                    <div className="mt-4">
                      <div className="mb-4">
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('admin.messageTitle')}
                        </label>
                        <input
                          type="text"
                          id="title"
                          value={messageTitle}
                          onChange={(e) => setMessageTitle(e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div className="mb-4">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('admin.message')}
                        </label>
                        <textarea
                          id="message"
                          rows={4}
                          value={messageContent}
                          onChange={(e) => setMessageContent(e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={sendingMessage}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {sendingMessage ? (
                    <>
                      <Send className="h-5 w-5 mr-2 animate-pulse" />
                      {t('admin.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      {t('admin.send')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsMessageModalOpen(false);
                    setMessageTitle('');
                    setMessageContent('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('admin.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEditSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {t('admin.editUser')}
                      </h3>
                      <div className="mt-4">
                        <div className="mb-4">
                          <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('admin.fullName')}
                          </label>
                          <input
                            type="text"
                            id="full_name"
                            value={editForm.full_name}
                            onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="points" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('admin.points')}
                          </label>
                          <input
                            type="number"
                            id="points"
                            value={editForm.points}
                            onChange={(e) => setEditForm({ ...editForm, points: parseInt(e.target.value) })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('admin.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('admin.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t('admin.userDetails')}
                    </h3>
                    <div className="mt-4">
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('admin.personalInfo')}</h4>
                        <dl className="grid grid-cols-2 gap-4">
                          <div>
                            <dt className="text-sm text-gray-500">{t('admin.fullName')}</dt>
                            <dd className="text-sm font-medium text-gray-900">{selectedUser.full_name}</dd>
                          </div>
                          <div>
                            <dt className="text-sm text-gray-500">{t('admin.email')}</dt>
                            <dd className="text-sm font-medium text-gray-900">{selectedUser.email}</dd>
                          </div>
                          <div>
                            <dt className="text-sm text-gray-500">{t('admin.country')}</dt>
                            <dd className="text-sm font-medium text-gray-900">{selectedUser.country}</dd>
                          </div>
                          <div>
                            <dt className="text-sm text-gray-500">{t('admin.joinedDate')}</dt>
                            <dd className="text-sm font-medium text-gray-900">{formatDate(selectedUser.created_at)}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('admin.statistics')}</h4>
                        <dl className="grid grid-cols-2 gap-4">
                          <div>
                            <dt className="text-sm text-gray-500">{t('admin.totalPoints')}</dt>
                            <dd className="text-sm font-medium text-gray-900">{selectedUser.total_points}</dd>
                          </div>
                          <div>
                            <dt className="text-sm text-gray-500">{t('admin.totalWaterLogged')}</dt>
                            <dd className="text-sm font-medium text-gray-900">{(selectedUser.water_volume_consumed / 1000).toFixed(2)}L</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('admin.rewardRequests')}</h4>
                        {userRewards.length === 0 ? (
                          <p className="text-sm text-gray-500">{t('admin.noRewardRequests')}</p>
                        ) : (
                          <ul className="space-y-2">
                            {userRewards.map((reward: any) => (
                              <li key={reward.id} className="text-sm">
                                <span className="font-medium text-gray-900">{reward.rewards.name}</span>
                                <span className="text-gray-500 ml-2">({formatDate(reward.created_at)})</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('admin.completedSurveys')}</h4>
                        {userSurveys.length === 0 ? (
                          <p className="text-sm text-gray-500">{t('admin.noCompletedSurveys')}</p>
                        ) : (
                          <ul className="space-y-2">
                            {userSurveys.map((survey: any) => (
                              <li key={survey.id} className="text-sm">
                                <span className="font-medium text-gray-900">{survey.surveys.title}</span>
                                <span className="text-gray-500 ml-2">({formatDate(survey.completed_at)})</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6">
                <button
                  type="button"
                  onClick={() => setIsViewModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                >
                  {t('admin.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t('admin.deleteUser')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t('admin.deleteUserConfirmation', { name: selectedUser.full_name })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('admin.delete')}
                </button>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('admin.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Users;